import fromEntries from 'fromentries'
import { RESTMethods } from '../../../../NonReact/constants/REST'
import { requestGetJSON } from '../../../../NonReact/common/API/REST/RESTAPIClient'
import { requestJSON } from '../../../../NonReact/common/API/REST/RESTClient'
import {
    CLEAR_CONTENTFUL_CACHE_URL,
    getAkixiDocumentURL,
    getHelpEntriesURL,
    QUEUE_INCOMPATIBILITIES_CONTENTFUL_URL,
    Tags
} from './Constants'
import {
    AkixiDocumentAsset,
    AkixiDocumentEntry,
    AssetEntry,
    ContentfulEntriesResponse,
    ContentfulEntryItem,
    HelpAssets,
    HelpEntry,
    IncompatibilitiesListContentful
} from './Types'
import { JSONResponse } from '../../../../NonReact/common/Types/Types'
import { COLON } from '../../Constants/Separators'

/**
 * It takes a tag, makes a request to Contentful, and returns a promise that
 * resolves to an object containing the assets and help entries
 *
 * @param {Tags} tag - Tags - The tag to filter the help entries by.
 */
export const getHelpEntries = (tag: Tags): Promise<Definable<HelpAssets>> =>
    requestGetJSON<ContentfulEntriesResponse<HelpEntry>>(getHelpEntriesURL(tag), {
        cacheTime: Infinity
    }).then(
        (response: JSONResponse<ContentfulEntriesResponse<HelpEntry>>): Definable<HelpAssets> => ({
            assets: fromEntries(
                response?.includes?.Asset?.map((item: ContentfulEntryItem<AssetEntry>): [string, AssetEntry] => [
                    item?.sys?.id,
                    item?.fields
                ]) ?? []
            ),
            helpEntries: fromEntries(
                response?.items?.map((item: ContentfulEntryItem<HelpEntry>): [string, HelpEntry] => [
                    item?.fields?.title,
                    item?.fields
                ]) ?? []
            )
        })
    )

/**
 * It takes a tag, makes a request to Contentful, and returns a promise that
 * resolves to an object containing the assets and Akixi Document entries
 *
 * @param {Tags} tag - Tags - The tag to filter the Akixi document entries by. Akixi Documents should only have one entry per tag.
 */
export const getAkixiDocumentEntry = (tag: Tags): Promise<Definable<AkixiDocumentAsset>> =>
    requestGetJSON<ContentfulEntriesResponse<AkixiDocumentEntry>>(getAkixiDocumentURL(tag), {
        cacheTime: Infinity
    }).then((response: JSONResponse<ContentfulEntriesResponse<AkixiDocumentEntry>>): Definable<AkixiDocumentAsset> => {
        const documentEntry: Definable<ContentfulEntryItem<AkixiDocumentEntry>> = response?.items?.[0]

        return {
            assets: fromEntries(
                response?.includes?.Asset?.map((item: ContentfulEntryItem<AssetEntry>): [string, AssetEntry] => [
                    item?.sys?.id,
                    item?.fields
                ]) ?? []
            ),
            entry: {
                title: documentEntry?.fields?.title,
                subtitle: documentEntry?.fields?.subtitle,
                content: documentEntry?.fields?.content
            }
        }
    })

export const getQueueIncompatibilitiesFromContentful = (): Promise<Definable<Record<string, string>>> =>
    requestGetJSON<ContentfulEntriesResponse<IncompatibilitiesListContentful>>(
        QUEUE_INCOMPATIBILITIES_CONTENTFUL_URL
    ).then(
        (
            response: JSONResponse<ContentfulEntriesResponse<IncompatibilitiesListContentful>>
        ): Definable<Record<string, string>> =>
            fromEntries(
                (response?.items?.[0]?.fields?.incompatibilityList ?? []).map(
                    (entry: string): [string, string] => entry.trim().split(COLON) as [string, string]
                )
            )
    )

/**
 * It makes a PUT request to the `/contentful/entries/clearCache` endpoint, which is a
 * URL that clears the Contentful cache
 */
export const clearContentfulCache = (): void => {
    requestJSON(CLEAR_CONTENTFUL_CACHE_URL, {
        method: RESTMethods.PUT
    })
}
